import type { TOC } from '@ember/component/template-only';

// ---------------------------------------------------------------------------
// Card – minimal wrapper for the standard reimagined card treatment
// ---------------------------------------------------------------------------

/**
 * Padding size options for the card
 */
export type CardPadding = 'none' | 'sm' | 'md' | 'lg';

interface CardSignature {
  Args: {
    /** Padding size (default: 'md' = p-5) */
    padding?: CardPadding;
    /** Whether to use rounded-xl (true) or rounded-lg (false). Default: true */
    roundedXl?: boolean;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLDivElement;
}

const paddingClasses: Record<CardPadding, string> = {
  none: '',
  sm: 'p-4',
  md: 'p-5',
  lg: 'p-6',
};

const getPadding = (padding: CardPadding = 'md') => paddingClasses[padding];

/**
 * Card - A minimal wrapper for the standard reimagined card treatment
 *
 * Replaces the repeated `bg-white rounded-xl border
 * border-gray-200 p-X` class string found in 20+ places.
 *
 * @example Basic usage
 * ```gts
 * import Card from 'tio-reimagined/components/card';
 *
 * <Card>
 *   <p>Card content</p>
 * </Card>
 * ```
 *
 * @example With large padding
 * ```gts
 * <Card @padding="lg">
 *   <p>Card content with more padding</p>
 * </Card>
 * ```
 */
const Card: TOC<CardSignature> = <template>
  <div
    data-test-card
    class="bg-white
      {{if @roundedXl 'rounded-xl' (if (eq @roundedXl false) 'rounded-lg' 'rounded-xl')}}
      border border-gray-200
      {{getPadding @padding}}"
    ...attributes
  >
    {{yield}}
  </div>
</template>;

// Simple equality helper for template
const eq = (a: unknown, b: unknown) => a === b;

export default Card;

export type { CardSignature };
